import { getAppRoutes, type AppRoute } from '@/routes';
import type { User } from '@/stores/auth';
import type { HelpGuideDefinition } from './types';

const HELP_GUIDES: HelpGuideDefinition[] = [
  {
    id: 'calendar_overview',
    titleKey: 'help_guides.guides.calendar_overview.title',
    subtitleKey: 'help_guides.guides.calendar_overview.subtitle',
    routePath: '/scale-management/calendar',
    startPath: '/scale-management/calendar?view=month',
    steps: [
      {
        selector: '#help-calendar-root',
        titleKey: 'help_guides.steps.calendar_overview.root.title',
        descriptionKey: 'help_guides.steps.calendar_overview.root.description',
        side: 'left',
      },
      {
        selector: '#calendar_header_specialty_id',
        titleKey: 'help_guides.steps.calendar_overview.specialty.title',
        descriptionKey:
          'help_guides.steps.calendar_overview.specialty.description',
        side: 'bottom',
      },
      {
        selector: '#calendar_header_department_id',
        titleKey: 'help_guides.steps.calendar_overview.department.title',
        descriptionKey:
          'help_guides.steps.calendar_overview.department.description',
        side: 'bottom',
      },
      {
        selector: '#help-calendar-view-switcher',
        titleKey: 'help_guides.steps.calendar_overview.views.title',
        descriptionKey: 'help_guides.steps.calendar_overview.views.description',
        side: 'bottom',
      },
      {
        selector: '[data-tour="calendar-month-cell"]',
        titleKey: 'help_guides.steps.calendar_overview.create_by_cell.title',
        descriptionKey: 'help_guides.steps.calendar_overview.create_by_cell.description',
        side: 'right',
      },
    ],
  },
  {
    id: 'calendar_create_scale',
    titleKey: 'help_guides.guides.calendar_create_scale.title',
    subtitleKey: 'help_guides.guides.calendar_create_scale.subtitle',
    routePath: '/scale-management/calendar',
    startPath: '/scale-management/calendar?view=month&openAddScale=1',
    steps: [
      {
        selector: '#help-add-scale-modal',
        titleKey: 'help_guides.steps.calendar_create_scale.modal.title',
        descriptionKey: 'help_guides.steps.calendar_create_scale.modal.description',
        side: 'left',
      },
      {
        selector: '#help-add-scale-department-field',
        titleKey: 'help_guides.steps.calendar_create_scale.department.title',
        descriptionKey: 'help_guides.steps.calendar_create_scale.department.description',
        side: 'bottom',
      },
      {
        selector: '#help-add-scale-speciality-field',
        titleKey: 'help_guides.steps.calendar_create_scale.speciality.title',
        descriptionKey: 'help_guides.steps.calendar_create_scale.speciality.description',
        side: 'bottom',
      },
      {
        selector: '#help-add-scale-save',
        titleKey: 'help_guides.steps.calendar_create_scale.save.title',
        descriptionKey: 'help_guides.steps.calendar_create_scale.save.description',
        side: 'left',
      },
    ],
  },
  {
    id: 'scheduled_shift_register',
    titleKey: 'help_guides.guides.scheduled_shift_register.title',
    subtitleKey: 'help_guides.guides.scheduled_shift_register.subtitle',
    routePath: '/scale-management/scheduled-shift/register',
    steps: [
      {
        selector: '#help-scheduled-shift-form',
        titleKey: 'help_guides.steps.scheduled_shift_register.form.title',
        descriptionKey: 'help_guides.steps.scheduled_shift_register.form.description',
        side: 'left',
      },
      {
        selector: '#department_id',
        titleKey: 'help_guides.steps.scheduled_shift_register.department.title',
        descriptionKey:
          'help_guides.steps.scheduled_shift_register.department.description',
        side: 'bottom',
      },
      {
        selector: '#specialty_id',
        titleKey: 'help_guides.steps.scheduled_shift_register.speciality.title',
        descriptionKey:
          'help_guides.steps.scheduled_shift_register.speciality.description',
        side: 'bottom',
      },
      {
        selector: '#help-scheduled-type',
        titleKey: 'help_guides.steps.scheduled_shift_register.type.title',
        descriptionKey: 'help_guides.steps.scheduled_shift_register.type.description',
        side: 'right',
      },
      {
        selector: '[data-testid="scheduled-shift-unique-start-date"]',
        titleKey: 'help_guides.steps.scheduled_shift_register.period.title',
        descriptionKey: 'help_guides.steps.scheduled_shift_register.period.description',
        side: 'right',
      },
      {
        selector: '#help-scheduled-professionals',
        titleKey: 'help_guides.steps.scheduled_shift_register.professionals.title',
        descriptionKey:
          'help_guides.steps.scheduled_shift_register.professionals.description',
        side: 'left',
      },
      {
        selector: '#help-scheduled-link-professional',
        titleKey: 'help_guides.steps.scheduled_shift_register.link_professionals.title',
        descriptionKey:
          'help_guides.steps.scheduled_shift_register.link_professionals.description',
        side: 'top',
      },
      {
        selector: '#save-btn',
        titleKey: 'help_guides.steps.scheduled_shift_register.save.title',
        descriptionKey: 'help_guides.steps.scheduled_shift_register.save.description',
        side: 'left',
      },
    ],
  },
  {
    id: 'records_management',
    titleKey: 'help_guides.guides.records_management.title',
    subtitleKey: 'help_guides.guides.records_management.subtitle',
    routePath: '/scale-management/records',
    steps: [
      {
        selector: '#help-records-header',
        titleKey: 'help_guides.steps.records_management.header.title',
        descriptionKey: 'help_guides.steps.records_management.header.description',
        side: 'bottom',
      },
      {
        selector: '#help-records-view-toggle',
        titleKey: 'help_guides.steps.records_management.views.title',
        descriptionKey: 'help_guides.steps.records_management.views.description',
        side: 'bottom',
      },
      {
        selector: '#help-records-date-picker',
        titleKey: 'help_guides.steps.records_management.date_picker.title',
        descriptionKey: 'help_guides.steps.records_management.date_picker.description',
        side: 'bottom',
      },
      {
        selector: '#help-records-today',
        titleKey: 'help_guides.steps.records_management.today.title',
        descriptionKey: 'help_guides.steps.records_management.today.description',
        side: 'bottom',
      },
      {
        selector: '#help-records-content',
        titleKey: 'help_guides.steps.records_management.content.title',
        descriptionKey: 'help_guides.steps.records_management.content.description',
        side: 'left',
      },
    ],
  },
  {
    id: 'establishment_professionals_actions',
    titleKey: 'help_guides.guides.establishment_professionals_actions.title',
    subtitleKey: 'help_guides.guides.establishment_professionals_actions.subtitle',
    routePath: '/my-establishment/establishment-professionals-linked',
    steps: [
      {
        selector: '#help-linked-professionals-grid',
        titleKey: 'help_guides.steps.establishment_professionals_actions.grid.title',
        descriptionKey:
          'help_guides.steps.establishment_professionals_actions.grid.description',
        side: 'left',
      },
      {
        selector: '#help-card-link-professionals',
        titleKey: 'help_guides.steps.establishment_professionals_actions.link.title',
        descriptionKey:
          'help_guides.steps.establishment_professionals_actions.link.description',
        side: 'right',
      },
      {
        selector: '#help-card-invite-professional',
        titleKey: 'help_guides.steps.establishment_professionals_actions.invite.title',
        descriptionKey:
          'help_guides.steps.establishment_professionals_actions.invite.description',
        side: 'right',
      },
      {
        selector: '#help-card-register-professional',
        titleKey: 'help_guides.steps.establishment_professionals_actions.register.title',
        descriptionKey:
          'help_guides.steps.establishment_professionals_actions.register.description',
        side: 'right',
      },
    ],
  },
  {
    id: 'establishment_specialities_register',
    titleKey: 'help_guides.guides.establishment_specialities_register.title',
    subtitleKey: 'help_guides.guides.establishment_specialities_register.subtitle',
    routePath: '/my-establishment/establishment-specialities/register',
    steps: [
      {
        selector: '#help-speciality-form',
        titleKey: 'help_guides.steps.establishment_specialities_register.form.title',
        descriptionKey:
          'help_guides.steps.establishment_specialities_register.form.description',
        side: 'left',
      },
      {
        selector: '#help-speciality-name',
        titleKey: 'help_guides.steps.establishment_specialities_register.name.title',
        descriptionKey:
          'help_guides.steps.establishment_specialities_register.name.description',
        side: 'bottom',
      },
      {
        selector: '#help-speciality-occupation-field',
        titleKey:
          'help_guides.steps.establishment_specialities_register.occupation.title',
        descriptionKey:
          'help_guides.steps.establishment_specialities_register.occupation.description',
        side: 'bottom',
      },
      {
        selector: '#help-speciality-rqe-field',
        titleKey: 'help_guides.steps.establishment_specialities_register.rqe.title',
        descriptionKey:
          'help_guides.steps.establishment_specialities_register.rqe.description',
        side: 'bottom',
      },
      {
        selector: '#help-speciality-save',
        titleKey: 'help_guides.steps.establishment_specialities_register.save.title',
        descriptionKey:
          'help_guides.steps.establishment_specialities_register.save.description',
        side: 'left',
      },
    ],
  },
  {
    id: 'establishment_sectors_register',
    titleKey: 'help_guides.guides.establishment_sectors_register.title',
    subtitleKey: 'help_guides.guides.establishment_sectors_register.subtitle',
    routePath: '/my-establishment/manage-sector-establishment/register',
    steps: [
      {
        selector: '#help-sector-form',
        titleKey: 'help_guides.steps.establishment_sectors_register.form.title',
        descriptionKey:
          'help_guides.steps.establishment_sectors_register.form.description',
        side: 'left',
      },
      {
        selector: '#help-sector-code',
        titleKey: 'help_guides.steps.establishment_sectors_register.code.title',
        descriptionKey:
          'help_guides.steps.establishment_sectors_register.code.description',
        side: 'bottom',
      },
      {
        selector: '#help-sector-name',
        titleKey: 'help_guides.steps.establishment_sectors_register.name.title',
        descriptionKey:
          'help_guides.steps.establishment_sectors_register.name.description',
        side: 'bottom',
      },
      {
        selector: '#help-sector-description',
        titleKey:
          'help_guides.steps.establishment_sectors_register.description.title',
        descriptionKey:
          'help_guides.steps.establishment_sectors_register.description.description',
        side: 'bottom',
      },
      {
        selector: '#help-sector-save',
        titleKey: 'help_guides.steps.establishment_sectors_register.save.title',
        descriptionKey:
          'help_guides.steps.establishment_sectors_register.save.description',
        side: 'left',
      },
    ],
  },
  {
    id: 'establishment_document_management',
    titleKey: 'help_guides.guides.establishment_document_management.title',
    subtitleKey: 'help_guides.guides.establishment_document_management.subtitle',
    routePath: '/my-establishment/establishment-document-management',
    steps: [
      {
        selector: '#help-document-management-form',
        titleKey: 'help_guides.steps.establishment_document_management.form.title',
        descriptionKey:
          'help_guides.steps.establishment_document_management.form.description',
        side: 'left',
      },
      {
        selector: '#help-document-type-0',
        titleKey:
          'help_guides.steps.establishment_document_management.document_type.title',
        descriptionKey:
          'help_guides.steps.establishment_document_management.document_type.description',
        side: 'bottom',
      },
      {
        selector: '#help-document-format-0',
        titleKey:
          'help_guides.steps.establishment_document_management.allowed_formats.title',
        descriptionKey:
          'help_guides.steps.establishment_document_management.allowed_formats.description',
        side: 'bottom',
      },
      {
        selector: '#help-document-required-field-0',
        titleKey: 'help_guides.steps.establishment_document_management.required.title',
        descriptionKey:
          'help_guides.steps.establishment_document_management.required.description',
        side: 'right',
      },
      {
        selector: '#help-document-management-add',
        titleKey: 'help_guides.steps.establishment_document_management.add.title',
        descriptionKey: 'help_guides.steps.establishment_document_management.add.description',
        side: 'top',
      },
      {
        selector: '#help-document-management-save',
        titleKey: 'help_guides.steps.establishment_document_management.save.title',
        descriptionKey: 'help_guides.steps.establishment_document_management.save.description',
        side: 'left',
      },
    ],
  },
];

function flattenRoutes(routes: AppRoute[]): AppRoute[] {
  return routes.flatMap((route) => {
    const children = route.children ? flattenRoutes(route.children) : [];
    return [route, ...children];
  });
}

// hasRequiredPermission removed — route availability from payload is the single source of truth

function getAvailableRoutePaths(user: User | null): Set<string> {
  if (!user) return new Set<string>();

  const routes = getAppRoutes(
    user.establishment_config_id ?? null,
    user.company_config_id ?? null,
    user.is_config_admin ?? false,
    user.is_company_admin ?? false,
    user.role_id === '1' || user.role === 'master',
  );

  return new Set(flattenRoutes(routes).map((route) => route.path));
}

export function getAccessibleHelpGuides(user: User | null): HelpGuideDefinition[] {
  const availablePaths = getAvailableRoutePaths(user);

  return HELP_GUIDES.filter((guide) => availablePaths.has(guide.routePath));
}

export function getHelpGuideById(
  user: User | null,
  id: string,
): HelpGuideDefinition | undefined {
  return getAccessibleHelpGuides(user).find((guide) => guide.id === id);
}
